import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * La linguetta dello zaino — la cinghia che si afferra per aprirlo.
 *
 * Vive in due posti e resta la stessa cosa: sopra la barra di navigazione
 * quando lo zaino è chiuso, in cima al pannello quando è aperto. È il motivo
 * per cui è un componente e non due pezzi di markup: **chi apre e chi chiude
 * devono essere lo stesso oggetto**, o l'apertura si legge come un comando e la
 * chiusura come un altro.
 *
 * Si può **toccare** o **trascinare** nella direzione giusta. Il trascinamento
 * non è un vezzo: una cinghia si tira, e chi prova a tirarla deve ottenere
 * qualcosa invece di scoprire che era solo un bottone.
 */
export function PackTab({
  open,
  onToggle,
  label,
  className,
}: {
  /** Aperto = la linguetta chiude e la freccia guarda in giù. */
  open: boolean;
  onToggle: () => void;
  label: string;
  className?: string;
}) {
  const haptics = useHaptics();
  const start = useRef<number | null>(null);
  /** Il trascinamento ha già agito: il tocco che segue va ignorato. */
  const done = useRef(false);
  /** Di quanto si è alzata la cinghia sotto il dito. Negativo = verso l'alto. */
  const [tira, setTira] = useState(0);
  const [inMano, setInMano] = useState(false);

  /** Quanto si deve tirare perché conti come gesto e non come tremolio. */
  const SOGLIA = 34;

  /**
   * DA APERTA la linguetta non trascina: trascina il pannello.
   *
   * Sono cuciti insieme (la linguetta è il `above` del foglio, dentro lo stesso
   * blocco animato), quindi il gesto lo raccoglie il foglio e tutto il blocco
   * scende sotto il dito. Gestirlo anche qui avrebbe fatto due cose per un
   * gesto solo: il pannello che segue la mano e la linguetta che a 34px lo
   * chiude di scatto sotto di essa.
   */
  const onPointerDown = (e: React.PointerEvent) => {
    if (open) return;
    start.current = e.clientY;
    done.current = false;
    setInMano(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (open || start.current === null || done.current) return;
    const dy = e.clientY - start.current;

    // La cinghia SEGUE il dito, non aspetta.
    // Prima non si muoveva affatto e a 34px lo zaino compariva: tirare non dava
    // nessuna risposta finché non ne dava una intera, ed è la ragione per cui
    // il gesto sembrava legnoso. Ora sale insieme alla mano — e si ferma alla
    // soglia, così il punto in cui «scatta» si sente prima di arrivarci.
    if (dy < 0) setTira(Math.max(dy, -SOGLIA));

    if (dy < -SOGLIA) {
      done.current = true;
      start.current = null;
      setTira(0);
      setInMano(false);
      haptics.impact('medium');
      onToggle();
    }
  };

  const end = () => {
    start.current = null;
    setTira(0);
    setInMano(false);
  };

  const onClick = () => {
    // Il trascinamento ha già aperto: il click che il browser manda al rilascio
    // richiuderebbe subito quello che è appena stato aperto.
    if (done.current) {
      done.current = false;
      return;
    }
    haptics.impact('light');
    onToggle();
  };

  const Freccia = open ? ChevronDown : ChevronUp;

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onPointerLeave={end}
      aria-expanded={open}
      style={{
        transform: tira ? `translateY(${tira}px)` : undefined,
        // Nessuna transizione mentre il dito è giù — la cinghia deve stare
        // SOTTO il polpastrello, non inseguirlo. Al rilascio torna a posto con
        // la stessa curva del foglio, così il rientro sembra elastico e non un
        // ritorno meccanico.
        transition: inMano ? 'none' : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
      className={cn(
        // Angoli arrotondati solo in ALTO: la linguetta esce da qualcosa, non
        // galleggia. È la stessa forma della barra sotto, in piccolo.
        // `touch-action:none`: il gesto è NOSTRO. Senza, tirando la cinghia il
        // browser interpreta lo stesso movimento come uno scorrimento della
        // pagina e ne annulla il seguito a metà.
        'flex select-none items-start justify-center gap-1.5 rounded-t-2xl px-6 [touch-action:none]',
        // Alta e con il contenuto in CIMA: il tondo della Hub sporge sopra la
        // barra, e con la linguetta bassa la scritta ci finiva sotto. Non è
        // sollevata — è più alta, quindi resta attaccata alla barra e il bottone
        // le passa davanti invece che sopra.
        'pb-5 pt-1.5',
        'bg-pack text-pack-fg transition-opacity active:opacity-90',
        // La cucitura: un filo chiaro lungo il bordo, il segno che dice cuoio
        // invece di plastica. Solo in alto e ai lati, come sul pannello.
        '[box-shadow:inset_0_1px_0_0_var(--color-pack-stitch),inset_1px_0_0_0_var(--color-pack-stitch),inset_-1px_0_0_0_var(--color-pack-stitch)]',
        className,
      )}
    >
      <Freccia aria-hidden className="h-4 w-4" strokeWidth={3} />
      {/* Pulsa solo da CHIUSA: da aperta il pannello è già lì e non c'è niente
          da annunciare — una scritta che pulsa su una cosa già visibile è
          rumore. */}
      <span className={cn('text-[11px] font-black uppercase tracking-wide', !open && 'pack-blink')}>
        {label}
      </span>
    </button>
  );
}
